import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Reply, Edit2, Trash2, Save, X, ArrowDownAZ, ArrowUpAZ, Send } from "lucide-react";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import Username from "@/components/Username";
import ReactionBar from "@/components/ReactionBar";

export default function Comments({ postSlug }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${postSlug}/comments`, { params: { sort } });
      setComments(data.comments || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [postSlug, sort]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!user || user === false) { toast.error("Login to comment"); nav("/login"); return; }
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/posts/${postSlug}/comments`, { content: text.trim() });
      setText("");
      await load();
      toast.success("Comment posted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="mt-12" data-testid="comments-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white inline-flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-rose-400" /> Discussion
          <span className="text-zinc-500 text-sm font-normal">({comments.length})</span>
        </h2>
        <button
          onClick={() => setSort(sort === "newest" ? "oldest" : "newest")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10"
          data-testid="comments-sort-toggle"
        >
          {sort === "newest" ? <ArrowDownAZ className="w-3.5 h-3.5" /> : <ArrowUpAZ className="w-3.5 h-3.5" />}
          {sort === "newest" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      {/* Composer */}
      <div className="glass rounded-xl p-4 mb-6">
        {user && user !== false ? (
          <div className="flex gap-3">
            <Avatar user={user} size={40} />
            <div className="flex-1">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Share your thoughts on this translation…"
                rows={3}
                className="bg-black/40 border-white/10"
                data-testid="comment-composer"
              />
              <div className="mt-2 flex justify-end">
                <Button onClick={post} disabled={submitting || !text.trim()} className="bg-rose-600 hover:bg-rose-500" data-testid="comment-submit">
                  <Send className="w-4 h-4 mr-2" /> {submitting ? "Posting…" : "Post comment"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-3 text-sm text-zinc-400">
            <button onClick={() => nav("/login")} className="text-rose-300 hover:underline">Log in</button> to join the discussion.
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mystic text-center py-6">Loading discussion…</div>
      ) : comments.length === 0 ? (
        <div className="text-zinc-500 text-center py-6 font-mystic">Be the first to speak the dawn.</div>
      ) : (
        <ul className="space-y-4">
          {comments.map((c) => (
            <CommentItem key={c.id} c={c} postSlug={postSlug} onChanged={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CommentItem({ c, postSlug, onChanged }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.content);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const me = user && user !== false ? user : null;
  const isMine = me && me.id === c.user_id;
  const isAdmin = me && me.role === "admin";

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.put(`/comments/${c.id}`, { content: text.trim() });
      setEditing(false);
      await onChanged();
      toast.success("Comment updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this comment?")) return;
    setBusy(true);
    try {
      await api.delete(`/comments/${c.id}`);
      await onChanged();
      toast.success("Comment deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/posts/${postSlug}/comments`, { content: replyText.trim(), parent_id: c.id });
      setReplyText("");
      setReplying(false);
      await onChanged();
      toast.success("Reply posted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <li className="glass rounded-xl p-4" data-testid={`comment-${c.id}`}>
      <CommentBody
        c={c}
        editing={editing}
        text={text}
        setText={setText}
        onSave={save}
        onCancelEdit={() => { setEditing(false); setText(c.content); }}
        onEdit={() => setEditing(true)}
        onDelete={del}
        onReply={() => setReplying(!replying)}
        canEdit={isMine}
        canDelete={isMine || isAdmin}
        canReply={!!me}
        busy={busy}
      />

      {replying && (
        <div className="mt-3 ml-12">
          <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply…" rows={2} className="bg-black/40 border-white/10" data-testid={`reply-input-${c.id}`} />
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setReplying(false); setReplyText(""); }}>Cancel</Button>
            <Button size="sm" onClick={reply} disabled={!replyText.trim() || busy} className="bg-rose-600 hover:bg-rose-500" data-testid={`reply-submit-${c.id}`}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> Reply
            </Button>
          </div>
        </div>
      )}

      {/* Replies */}
      {(c.replies || []).length > 0 && (
        <ul className="mt-3 ml-12 space-y-3 border-l border-white/10 pl-4">
          {c.replies.map((r) => (
            <ReplyItem key={r.id} r={r} postSlug={postSlug} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ReplyItem({ r, postSlug, onChanged }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.content);
  const [busy, setBusy] = useState(false);
  const me = user && user !== false ? user : null;
  const isMine = me && me.id === r.user_id;
  const isAdmin = me && me.role === "admin";

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/comments/${r.id}`, { content: text.trim() });
      setEditing(false);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this reply?")) return;
    setBusy(true);
    try {
      await api.delete(`/comments/${r.id}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <li data-testid={`reply-${r.id}`}>
      <CommentBody
        c={r}
        compact
        editing={editing}
        text={text}
        setText={setText}
        onSave={save}
        onCancelEdit={() => { setEditing(false); setText(r.content); }}
        onEdit={() => setEditing(true)}
        onDelete={del}
        onReply={null}
        canEdit={isMine}
        canDelete={isMine || isAdmin}
        canReply={false}
        busy={busy}
      />
    </li>
  );
}

function CommentBody({ c, editing, text, setText, onSave, onCancelEdit, onEdit, onDelete, onReply, canEdit, canDelete, canReply, busy, compact = false }) {
  return (
    <div className="flex gap-3">
      <Avatar
        user={{ avatar_url: c.user_avatar, name: c.user_name, role: c.user_role, badges: c.user_badges }}
        size={compact ? 34 : 40}
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Username name={c.user_name} role={c.user_role} className="font-display font-bold text-white text-sm" />
          <RoleBadge role={c.user_role} badges={c.user_badges} size="xs" />
          <span className="text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</span>
          {c.edited && <span className="text-[10px] text-zinc-500 italic">(edited)</span>}
        </div>

        {editing ? (
          <div className="mt-2">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="bg-black/40 border-white/10" />
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={onSave} disabled={busy} className="bg-rose-600 hover:bg-rose-500"><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={onCancelEdit}><X className="w-3.5 h-3.5 mr-1.5" /> Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="mt-1.5 text-sm text-zinc-200 whitespace-pre-wrap break-words">{c.content}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <ReactionBar targetType="comment" targetId={c.id} initial={c.reactions} size="sm" />
          {canReply && onReply && (
            <button onClick={onReply} className="text-xs text-zinc-400 hover:text-rose-300 inline-flex items-center gap-1" data-testid={`reply-btn-${c.id}`}>
              <Reply className="w-3.5 h-3.5" /> Reply
            </button>
          )}
          {canEdit && !editing && (
            <button onClick={onEdit} className="text-xs text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1" data-testid={`edit-btn-${c.id}`}>
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {canDelete && (
            <button onClick={onDelete} className="text-xs text-zinc-400 hover:text-rose-400 inline-flex items-center gap-1" data-testid={`delete-btn-${c.id}`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
